import {useEffect, useId, useRef, useState} from 'react';
import {useForm} from 'react-hook-form';
import {useMutation} from '@tanstack/react-query';
import {SiteHeader} from '@common/ui/navigation/site-header';
import {useUser} from '../use-user';
import {ProgressCircle} from '@common/ui/progress/progress-circle';
import {Trans} from '@common/i18n/trans';
import {StaticPageTitle} from '@common/seo/static-page-title';
import {TwoFactorStepper} from '@common/auth/ui/two-factor/stepper/two-factor-auth-stepper';
import {Form} from '@common/ui/forms/form';
import {useUploadAvatar} from '@common/auth/ui/account-settings/avatar/upload-avatar';
import {useRemoveAvatar} from '@common/auth/ui/account-settings/avatar/remove-avatar';
import {useUpdateAccountDetails} from '@common/auth/ui/account-settings/basic-info-panel/update-account-details';
import {
  UpdatePasswordPayload,
  useUpdatePassword,
} from '@common/auth/ui/account-settings/change-password-panel/use-update-password';
import {apiClient, queryClient} from '@common/http/query-client';
import {toast} from '@common/ui/toast/toast';
import {message} from '@common/i18n/message';
import {UploadedFile} from '@common/uploads/uploaded-file';
import {onFormQueryError} from '@common/errors/on-form-query-error';
import axios from 'axios';
import {BackendErrorResponse} from '@common/errors/backend-error-response';
import {
  ActiveSession,
  useUserSessions,
} from '@common/auth/ui/account-settings/sessions-panel/requests/use-user-sessions';
import {useLogoutOtherSessions} from '@common/auth/ui/account-settings/sessions-panel/requests/use-logout-other-sessions';
import {usePasswordConfirmedAction} from '@common/auth/ui/confirm-password/use-password-confirmed-action';
import {useLogoutSession} from '@common/auth/ui/account-settings/sessions-panel/requests/use-logout-session';
import {ComputerIcon} from '@common/icons/material/Computer';
import {SmartphoneIcon} from '@common/icons/material/Smartphone';
import {TabletIcon} from '@common/icons/material/Tablet';
import {DialogTrigger} from '@common/ui/overlays/dialog/dialog-trigger';
import {ConfirmationDialog} from '@common/ui/overlays/dialog/confirmation-dialog';
import {useDeleteAccount} from '@common/auth/ui/account-settings/danger-zone-panel/delete-account';
import {
  getUserInitials,
  hasUploadedAvatar,
} from '@common/ui/navigation/navbar-new';

function getFirstBackendErrorMessage(err: unknown): string {
  if (axios.isAxiosError(err) && err.response?.data) {
    const response = err.response.data as BackendErrorResponse;
    const firstFieldErrors = Object.values(response.errors || {})[0];
    if (typeof firstFieldErrors === 'string' && firstFieldErrors) {
      return firstFieldErrors;
    }
    if (Array.isArray(firstFieldErrors) && firstFieldErrors[0]) {
      return String(firstFieldErrors[0]);
    }
    if (response.message) return response.message;
  }
  return 'There was an issue. Please try again.';
}

function isCurrentDeviceSession(session: ActiveSession): boolean {
  const flag = (session as ActiveSession & {is_current_device?: unknown})
    .is_current_device;
  return (
    flag === true || flag === 1 || flag === '1' || String(flag) === 'true'
  );
}

export function AccountSettingsPageFigma() {
  const [activeTab, setActiveTab] = useState<
    'personal' | 'twofactor' | 'sessions' | 'delete'
  >('personal');
  const {data, isLoading} = useUser('me', {
    with: ['roles', 'social_profiles', 'tokens'],
  });

  const user = data?.user;
  const fullName = user?.display_name || user?.name || 'Testing Account';
  const firstName = user?.first_name || fullName.split(' ')[0] || 'Testing';
  const lastName = user?.last_name || '';
  const email = user?.email || '';
  const [profileName, setProfileName] = useState(firstName);
  const initials = getUserInitials(user ?? undefined);
  const nameFormId = useId();
  const passwordFormId = useId();
  const nameForm = useForm<{first_name: string; last_name: string; email: string}>({
    defaultValues: {
      first_name: firstName,
      last_name: lastName,
      email,
    },
  });
  const passwordForm = useForm<UpdatePasswordPayload>({
    defaultValues: {
      current_password: '',
      password: '',
      password_confirmation: '',
    },
  });
  const updateDetails = useUpdateAccountDetails(nameForm as any);
  const updatePassword = useUpdatePassword(passwordForm);
  const uploadAvatar = useUploadAvatar({user: user!});
  const removeAvatar = useRemoveAvatar({user: user!});
  const deleteAccount = useDeleteAccount();
  const userSessions = useUserSessions();
  const logoutOther = useLogoutOtherSessions();
  const logoutSession = useLogoutSession();
  const {withConfirmedPassword, isLoading: checkingPasswordStatus} =
    usePasswordConfirmedAction({needsPassword: true});
  const updateEmail = useMutation(
    (value: string) => apiClient.put('users/me', {email: value.trim()}).then(r => r.data),
    {
      onError: err => {
        onFormQueryError(err, nameForm as any);
        toast.danger(getFirstBackendErrorMessage(err));
      },
    },
  );
  const [avatarInputKey, setAvatarInputKey] = useState(0);
  const [avatarPreviewUrl, setAvatarPreviewUrl] = useState<string | null>(
    user?.avatar || null,
  );
  const [avatarFailed, setAvatarFailed] = useState(false);
  const blobPreviewRef = useRef<string | null>(null);

  useEffect(() => {
    setAvatarFailed(false);
  }, [avatarPreviewUrl]);

  useEffect(() => {
    if (user?.avatar) {
      setAvatarPreviewUrl(user.avatar);
    }
  }, [user?.avatar]);

  useEffect(() => {
    setProfileName(firstName);
  }, [firstName]);

  useEffect(() => {
    if (!user) return;
    nameForm.reset({
      first_name: user.first_name || '',
      last_name: user.last_name || '',
      email: user.email || '',
    });
  }, [user, nameForm]);

  useEffect(() => {
    return () => {
      if (blobPreviewRef.current) {
        URL.revokeObjectURL(blobPreviewRef.current);
      }
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#f5f5f7] text-main dark:bg-[#14161a] dark:text-[#f3f4f6]">
      <StaticPageTitle>
        <Trans message="Account Settings" />
      </StaticPageTitle>
      <SiteHeader />
      <div className="relative mx-auto w-full max-w-[1895px] px-[6px] pb-[24px] lg:px-[12px] lg:pb-[25px]">
        {isLoading || !user ? (
          <div className="flex min-h-[60vh] items-center justify-center">
            <ProgressCircle aria-label="Loading user.." isIndeterminate />
          </div>
        ) : (
          <div className="mx-auto mt-24 max-w-[1207px] rounded-[40px] border-2 border-divider bg-paper p-20 md:mt-40 md:min-h-[709px] md:rounded-[60px] md:p-28">
            <div className="grid grid-cols-1 gap-20 lg:grid-cols-[210px_minmax(0,1fr)] lg:gap-28">
              <aside className="rounded-[24px] border-2 border-divider bg-paper py-10 md:h-[269px]">
                <button
                  type="button"
                  onClick={() => setActiveTab('personal')}
                  className={`mx-8 flex h-[65px] w-[calc(100%-16px)] items-center justify-center rounded-[22px] px-6 text-center text-[16px] leading-none ${
                    activeTab === 'personal'
                      ? 'border-2 border-[#2b7e20] font-bold text-[#2b7e20]'
                      : 'text-main'
                  }`}
                >
                  Personal Info
                </button>
                <button
                  type="button"
                  onClick={() => setActiveTab('twofactor')}
                  className={`mx-8 mt-8 flex h-[52px] w-[calc(100%-16px)] items-center justify-center whitespace-nowrap rounded-[22px] border-2 px-4 text-center text-[14px] leading-none ${
                    activeTab === 'twofactor'
                      ? 'border-[#2b7e20] font-bold text-[#2b7e20]'
                      : 'border-transparent text-main'
                  }`}
                >
                  Two factor authentication
                </button>
                <button
                  type="button"
                  onClick={() => setActiveTab('sessions')}
                  className={`mx-8 mt-8 flex h-[52px] w-[calc(100%-16px)] items-center justify-center rounded-[22px] border-2 px-6 text-center text-[16px] leading-none ${
                    activeTab === 'sessions'
                      ? 'border-[#2b7e20] font-bold text-[#2b7e20]'
                      : 'border-transparent text-main'
                  }`}
                >
                  Active sessions
                </button>
                <button
                  type="button"
                  onClick={() => setActiveTab('delete')}
                  className={`mx-8 mt-8 flex h-[52px] w-[calc(100%-16px)] items-center justify-center rounded-[22px] border-2 px-6 text-center text-[16px] leading-none ${
                    activeTab === 'delete'
                      ? 'border-[#2b7e20] font-bold text-[#2b7e20]'
                      : 'border-transparent text-main'
                  }`}
                >
                  Delete account
                </button>
              </aside>

              <main>
                {activeTab === 'personal' && (
                  <>
                    <div className="mb-16 flex flex-col gap-16 md:flex-row md:items-start md:justify-between">
                      <div className="flex items-center gap-16">
                        {avatarPreviewUrl &&
                        !avatarFailed &&
                        hasUploadedAvatar(avatarPreviewUrl) ? (
                          <img
                            src={avatarPreviewUrl}
                            alt={fullName}
                            onError={() => setAvatarFailed(true)}
                            className="h-[120px] w-[120px] rounded-[40px] border-2 border-[#b8e1ff] object-cover md:h-[160px] md:w-[160px] md:rounded-[60px]"
                          />
                        ) : (
                          <div className="flex h-[120px] w-[120px] items-center justify-center rounded-[40px] border-2 border-[#b8e1ff] bg-[#d7eeff] text-[36px] font-medium text-[#3ea4f2] md:h-[160px] md:w-[160px] md:rounded-[60px]">
                            {initials}
                          </div>
                        )}
                        <div className="min-w-[210px]">
                          <input
                            key={avatarInputKey}
                            id="account-avatar-upload"
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={e => {
                              const file = e.target.files?.[0];
                              if (!file) return;
                              if (blobPreviewRef.current) {
                                URL.revokeObjectURL(blobPreviewRef.current);
                              }
                              const previewUrl = URL.createObjectURL(file);
                              blobPreviewRef.current = previewUrl;
                              setAvatarPreviewUrl(previewUrl);
                              uploadAvatar.mutate(
                                {file: {native: file} as UploadedFile},
                                {
                                  onSuccess: response => {
                                    if (blobPreviewRef.current) {
                                      URL.revokeObjectURL(blobPreviewRef.current);
                                      blobPreviewRef.current = null;
                                    }
                                    setAvatarPreviewUrl(
                                      response.user?.avatar || null,
                                    );
                                  },
                                },
                              );
                              setAvatarInputKey(prev => prev + 1);
                            }}
                          />
                          <button
                            type="button"
                            onClick={() => {
                              const input = document.getElementById(
                                'account-avatar-upload',
                              ) as HTMLInputElement | null;
                              input?.click();
                            }}
                            className="h-[65px] w-full rounded-[24px] border-2 border-divider bg-paper px-20 text-[17px] text-main transition-colors hover:border-[#2b7e20]"
                          >
                            Change profile picture
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setAvatarPreviewUrl(null);
                              removeAvatar.mutate();
                            }}
                            className="mt-8 text-[12px] text-muted underline"
                          >
                            Remove picture
                          </button>
                        </div>
                      </div>
                      <h1 className="max-w-[220px] text-[25px] font-bold leading-tight text-main">
                        Good day,
                        <br />
                        {profileName}
                      </h1>
                    </div>

                    <div className="max-w-[648px] space-y-14">
                      <Form
                        form={nameForm}
                        id={nameFormId}
                        onSubmit={values => {
                          updateDetails.mutate(
                            {
                              first_name: values.first_name,
                              last_name: values.last_name,
                            },
                            {
                              onError: err => {
                                toast.danger(getFirstBackendErrorMessage(err));
                              },
                            },
                          );
                          setProfileName((values.first_name || '').trim() || firstName);
                          if (values.email.trim() && values.email !== email) {
                            updateEmail.mutate(values.email, {
                              onSuccess: () => toast(message('Updated account details')),
                            });
                          }
                        }}
                        className="space-y-14"
                      >
                        <div className="grid grid-cols-1 gap-10 md:grid-cols-[400px_210px] md:items-center md:gap-38">
                          <div className="grid min-w-0 grid-cols-2 gap-10">
                            <input
                              {...nameForm.register('first_name')}
                            className="h-[65px] min-w-0 rounded-[24px] border-2 border-transparent bg-alt px-20 text-[17px] text-main outline-none transition-colors placeholder:text-muted focus:border-[#2b7e20]"
                              placeholder="First name"
                            />
                            <input
                              {...nameForm.register('last_name')}
                            className="h-[65px] min-w-0 rounded-[24px] border-2 border-transparent bg-alt px-20 text-[17px] text-main outline-none transition-colors placeholder:text-muted focus:border-[#2b7e20]"
                              placeholder="Last name"
                            />
                        </div>
                          <button
                            type="submit"
                            className="h-[65px] rounded-[24px] border-2 border-divider bg-paper px-20 text-[17px] text-main transition-colors hover:border-[#2b7e20]"
                          >
                            Change your name
                          </button>
                        </div>

                        <div className="flex flex-col gap-10 md:flex-row md:items-center md:gap-38">
                          <input
                            {...nameForm.register('email')}
                            className="h-[65px] rounded-[24px] border-2 border-transparent bg-alt px-20 text-[17px] text-main outline-none transition-colors placeholder:text-muted focus:border-[#2b7e20] md:w-[400px]"
                            placeholder="Email"
                            type="email"
                          />
                          <button
                            type="submit"
                            className="h-[65px] rounded-[24px] border-2 border-divider bg-paper px-20 text-[17px] text-main transition-colors hover:border-[#2b7e20] md:w-[210px]"
                          >
                            Change email
                          </button>
                        </div>
                      </Form>

                      <Form
                        form={passwordForm}
                        id={passwordFormId}
                        onSubmit={values => {
                          updatePassword.mutate(
                            {
                              ...values,
                              password_confirmation: values.password,
                            },
                            {
                              onSuccess: () => passwordForm.reset(),
                              onError: err => {
                                toast.danger(getFirstBackendErrorMessage(err));
                              },
                            },
                          );
                        }}
                        className="space-y-14"
                      >
                        <div className="flex flex-col gap-10 md:flex-row md:items-center md:gap-38">
                          <input
                            {...passwordForm.register('current_password')}
                            className="h-[65px] rounded-[24px] border-2 border-transparent bg-alt px-20 text-[17px] text-main outline-none transition-colors placeholder:text-muted focus:border-[#2b7e20] md:w-[400px]"
                            type="password"
                            placeholder="Current password:"
                            autoComplete="current-password"
                          />
                        </div>

                        <div className="flex flex-col gap-10 md:flex-row md:items-center md:gap-38">
                          <input
                            {...passwordForm.register('password')}
                            className="h-[65px] rounded-[24px] border-2 border-transparent bg-alt px-20 text-[17px] text-main outline-none transition-colors placeholder:text-muted focus:border-[#2b7e20] md:w-[400px]"
                            type="password"
                            placeholder="New password:"
                            autoComplete="new-password"
                          />
                          <button
                            type="submit"
                            className="h-[65px] rounded-[24px] border-2 border-divider bg-paper px-20 text-[17px] text-main transition-colors hover:border-[#2b7e20] md:w-[210px]"
                          >
                            Reset password
                          </button>
                        </div>
                      </Form>
                    </div>
                  </>
                )}
                {activeTab === 'twofactor' && (
                  <div className="rounded-[24px] border-2 border-divider bg-paper p-20 [&_svg]:block [&_svg]:h-full [&_svg]:w-full">
                    <h2 className="mb-16 text-[24px] font-bold text-main">
                      Two factor authentication
                    </h2>
                    <TwoFactorStepper user={user} />
                  </div>
                )}
                {activeTab === 'sessions' && (
                  <div className="rounded-[24px] border-2 border-divider bg-paper p-20">
                    <h2 className="mb-16 text-[16px] font-bold leading-tight text-main">
                      Below are all devices currently logged in to your CloudTransfer
                      account. If necessary you may logout of your other browser
                      sessions. If you believe your account may have been compromised,
                      you should also update your password.
                    </h2>

                    {userSessions.isLoading ? (
                      <ProgressCircle
                        className="my-30"
                        aria-label="Loading sessions.."
                        isIndeterminate
                      />
                    ) : (
                      <div className="max-h-[300px] overflow-y-auto pr-10 [scrollbar-width:thin] [scrollbar-color:#e9ecf0_transparent]">
                        <div className="space-y-16">
                          {userSessions.data?.sessions?.map(session => (
                            <SessionChipRow
                              key={session.id}
                              session={session}
                              onRemoveSession={() => {
                                logoutSession.mutate(Number(session.id), {
                                  onSuccess: () => {
                                    queryClient.invalidateQueries(['user-sessions']);
                                    toast(message('Session logged out.'));
                                  },
                                  onError: err => {
                                    toast.danger(getFirstBackendErrorMessage(err));
                                  },
                                });
                              }}
                            />
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="mt-24 flex justify-center">
                      <button
                        type="button"
                        disabled={checkingPasswordStatus || logoutOther.isLoading}
                        onClick={() => {
                          withConfirmedPassword(password => {
                            logoutOther.mutate(
                              {password: password!},
                              {
                                onSuccess: () => {
                                  toast(message('Logged out other sessions.'));
                                },
                                onError: err => {
                                  toast.danger(getFirstBackendErrorMessage(err));
                                },
                              },
                            );
                          });
                        }}
                        className="h-[52px] rounded-[20px] bg-[#2b7e20] px-24 text-[14px] font-medium text-white disabled:opacity-70"
                      >
                        Logout other sessions
                      </button>
                    </div>
                  </div>
                )}
                {activeTab === 'delete' && (
                  <div className="rounded-[24px] border-2 border-divider bg-paper p-20">
                    <div className="mx-auto max-w-[811px] rounded-[24px] border-2 border-divider px-20 py-24 md:px-28 md:py-24">
                      <p className="text-center text-[16px] font-bold text-main">
                        Ready to say goodbye? We made deleting your account easy and fast.
                      </p>
                      <div className="mx-auto mt-20 max-w-[320px] text-[16px] font-bold leading-[1.35] text-main">
                        <p className="mb-10 text-center">Deleting account will:</p>
                        <p>Remove your account from our database</p>
                        <p>Remove all your identifiable data</p>
                        <p>Cancel your current paid plan</p>
                        <p>All files saved in Drive will be deleted</p>
                      </div>
                      <div className="mt-24 flex justify-center">
                        <DialogTrigger
                          type="modal"
                          onClose={isConfirmed => {
                            if (isConfirmed) {
                              deleteAccount.mutate();
                            }
                          }}
                        >
                          <button
                            type="button"
                            className="h-[52px] rounded-[20px] bg-[#d64242] px-18 text-[16px] font-normal text-white"
                          >
                            Delete account
                          </button>
                          <ConfirmationDialog
                            isDanger
                            title={<Trans message="Delete account?" />}
                            body={
                              <Trans message="Your account will be deleted immediately and permanently. Once deleted, accounts can not be restored." />
                            }
                            confirm={<Trans message="Delete" />}
                          />
                        </DialogTrigger>
                      </div>
                    </div>
                  </div>
                )}
              </main>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

interface SessionChipRowProps {
  session: ActiveSession;
  onRemoveSession: () => void;
}
function SessionChipRow({session, onRemoveSession}: SessionChipRowProps) {
  const deviceText = `${session.device_type || ''} ${session.platform || ''} ${
    session.browser || ''
  }`.toLowerCase();
  const deviceLabel =
    session.device_type === 'mobile' ||
    deviceText.includes('ios') ||
    deviceText.includes('android') ||
    deviceText.includes('mobile') ||
    deviceText.includes('iphone')
      ? 'Mobile'
      : session.device_type === 'tablet' ||
        deviceText.includes('ipad') ||
        deviceText.includes('tablet')
      ? 'Tablet'
      : 'Computer';
  const locationLabel = [session.city, session.country].filter(Boolean).join(', ');
  return (
    <div className="grid grid-cols-[24px_minmax(0,1fr)] items-center gap-12 md:grid-cols-[40px_1fr]">
      <div className="flex h-[24px] w-[24px] items-center justify-center text-muted md:h-[40px] md:w-[40px]">
        {deviceLabel === 'Mobile' ? (
          <SmartphoneIcon size="md" />
        ) : deviceLabel === 'Tablet' ? (
          <TabletIcon size="md" />
        ) : (
          <ComputerIcon size="md" />
        )}
      </div>
      <div className="flex flex-wrap items-center gap-8">
        <span className="rounded-[24px] border-2 border-divider bg-alt px-12 py-8 text-[13px] leading-none text-main">
          {locationLabel || 'Unknown location'}
        </span>
        <span className="rounded-[24px] border-2 border-divider bg-alt px-12 py-8 text-[13px] leading-none text-main">
          {session.ip_address || 'Unknown IP'}
        </span>
        {isCurrentDeviceSession(session) ? (
          <span className="rounded-[24px] border-2 border-divider bg-alt px-12 py-8 text-[13px] font-bold leading-none text-[#2b7e20]">
            This device
          </span>
        ) : (
          <button
            type="button"
            onClick={onRemoveSession}
            className="inline-flex h-[20px] w-[20px] items-center justify-center rounded-full bg-[#cd5929] text-[12px] font-bold text-white"
            aria-label="Logout session"
          >
            x
          </button>
        )}
      </div>
    </div>
  );
}

